import React, { useEffect, Dispatch, SetStateAction } from "react";
import { Form, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import LoadingContent from "../../LoadingContent";
import { countryCodeToFlag } from "../../../utils/Admin";
import { toast } from "react-toastify";
import Button from "../../../components/Button";

interface InstagramProps {
  data: any;
  instagramEmbed: string;
  setInstagramEmbed: Dispatch<SetStateAction<string>>;
  onIndividualSubmit: () => void; // Recibido del padre para envío 1 a 1
  uploading: boolean; // Recibido del padre para el spinner individual
  selectedImages: any[];
  setSelectedImages: any;
  hastaghs: string[];
}

const UploadRaceInstagram = ({
  data,
  instagramEmbed,
  setInstagramEmbed,
  onIndividualSubmit,
  uploading,
  selectedImages,
  setSelectedImages,
  hastaghs,
}: InstagramProps) => {
  const MAX_CHARS = 2200;

  const charCount = instagramEmbed.length;
  const isOverLimit = charCount > MAX_CHARS;

  const getRaceName = (length: number, type_race: number) => {
    if (length === 2 && type_race === 1) return "Main Race";
    else if (length === 2 && type_race === 2) return "Sprint Race";
  };

  useEffect(() => {
    if (!data || !data.winners) return;
    setInstagramEmbed(
      data.winners.length === 1
        ? [
            `${countryCodeToFlag(data.grand_prix.grand_prix.flag.code)} ${data.grand_prix.name.toUpperCase()} is over.`,
            "",
            `${
              data.winners[0].flag_code
                ? countryCodeToFlag(data.winners[0].flag_code)
                : "🏁"
            } ${data.winners[0].username} takes the victory 🏆`,
            `${
              data.race_results[1].flag_code
                ? countryCodeToFlag(data.race_results[1].flag_code)
                : "🏁"
            } ${data.race_results[1].username} (2º) 🥈`,
            `${
              data.race_results[2].flag_code
                ? countryCodeToFlag(data.race_results[2].flag_code)
                : "🏁"
            } ${data.race_results[2].username} (3º) 🥉`,
            "",
            hastaghs.join(" "),
          ].join("\n")
        : data.winners[0].class_name == null
          ? [
              `${countryCodeToFlag(data.grand_prix.grand_prix.flag.code)} ${data.grand_prix.name.toUpperCase()} is over.`,
              "",
              ...data.winners.map((winner: any) => {
                const flag = winner.flag_code
                  ? countryCodeToFlag(winner.flag_code)
                  : "🏁";

                const position = "🏆";

                return `${flag} ${winner.username} in ${getRaceName(
                  data.winners.length,
                  winner.type_race,
                )} ${position}`;
              }),
              "",
              hastaghs.join(" "),
            ].join("\n")
          : [
              `${countryCodeToFlag(data.grand_prix.grand_prix.flag.code)} ${data.grand_prix.name.toUpperCase()} is over.`,
              "",
              ...data.winners.map((winner: any, index: number) => {
                const flag = winner.flag_code
                  ? countryCodeToFlag(winner.flag_code)
                  : "🏁";
                return `🏆 ${winner.class_name} Winner: ${flag} ${winner.username}`;
              }),
              "",
              hastaghs.join(" "),
            ].join("\n"),
    );
  }, [data, setInstagramEmbed]);

  if (data == null) return <LoadingContent />;

  return (
    // Cambiado de <Form> a un <div> plano para integrarse con el padre
    <div className="instagram-form mb-4 p-3 border rounded shadow-sm">
      <Row className="mb-3">
        <Form.Group className="form-group col-md-12">
          <h4>
            <FontAwesomeIcon icon={faInstagram} className="me-2" />
            Instagram [Including GP Image]
          </h4>
        </Form.Group>

        <Form.Group className="form-group" as={Col} md="12">
          <div
            style={{
              marginTop: "6px",
              fontSize: "13px",
              textAlign: "right",
              color: isOverLimit ? "red" : "#e2e2e2",
              fontWeight: isOverLimit ? "bold" : "normal",
            }}
          >
            {charCount} / {MAX_CHARS}
          </div>
          <Form.Label>Post (*)</Form.Label>
          <Form.Control
            required
            as="textarea"
            name="instagram_embed"
            rows={6}
            value={instagramEmbed}
            onChange={(e) => setInstagramEmbed(e.target.value)}
            isInvalid={isOverLimit}
          />
          <Form.Control.Feedback type="invalid">
            Field required o excess characters
          </Form.Control.Feedback>
        </Form.Group>
      </Row>

      <div className="form-group">
        <label
          htmlFor="instagram-images"
          className="block text-sm font-medium text-gray-700"
        >
          [OPTIONAL] Select the custom images for the post (Max. 10 for
          Instagram)
        </label>
        <input
          id="instagram-images"
          type="file"
          accept="image/*"
          multiple // <- Clave para permitir más de una foto
          onChange={(e: any) => {
            const files = Array.from(e.target.files);
            if (files.length > 10) {
              return toast.error(
                "Instagram solo permite un máximo de 10 imágenes",
              );
            }
            setSelectedImages(files);
          }}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
        />

        {/* Opcional: Una pequeña vista previa de las imágenes seleccionadas */}
        {selectedImages.length > 0 && (
          <div className="insta-images-container flex gap-2 mt-2 overflow-x-auto">
            {selectedImages.map((file, index) => (
              <img
                key={index}
                src={URL.createObjectURL(file)}
                alt="preview"
                className="w-16 h-16 object-cover rounded"
              />
            ))}
          </div>
        )}
      </div>
      {/* Botón para enviar ÚNICAMENTE Instagram */}
      <div className="text-center">
        {uploading ? (
          <FontAwesomeIcon
            icon={faSpinner}
            className="spinner-button in-button"
            spin
          />
        ) : (
          <Button
            text={"Send Instagram Post Only"}
            onClick={onIndividualSubmit}
            disabled={isOverLimit}
            className="button-neutral-lvf1"
          />
        )}
      </div>
    </div>
  );
};

export default UploadRaceInstagram;
